import * as React from 'react';
import withStyles from 'react-jss';
import { CssType } from 'src/theme/jss-types';
import { Upload, Button } from 'antd';
import { handleXLSXFileWithoutMapping } from 'src/utils/utils';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import classNames from 'classnames';
import { withTranslation } from 'react-i18next';

import uploadIcon from '../../assets/icons/upload.svg';

const { Dragger } = Upload;

type CustomerRequestArgs = {
  onSuccess?: any;
  onError?: any;
  file?: any;
};

interface ExcelUploadProps {
  sampleLink?: string;
  sampleApi?: () => void;
  classes: any;
  showButtonOnly?: boolean;
  isDisabled?: boolean;
  title?: string;
  additionalUploadParams?: any;
  t: (key: string) => string;
  onDataParsed?: (dataList: any[], file: any) => void;
  onRemove?: () => void;
  firstRowAsHeader?: boolean;
}

const styles = (): CssType => ({
  link: {
    color: '#1890ff',
    cursor: 'pointer',
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  downloadDiv: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 10,
    '& a': {
      color: '#1890ff',
    },
  },
  dragger: {
    padding: 16,
  },
  uploadHint: {
    marginBottom: 20,
    fontSize: 14,
  },
  uploadIcon: {
    height: '3em',
  },
  uploadText: {
    color: '#525252',
    marginBottom: 30,
    fontSize: 14,
    fontWeight: 400,
  },
  browseText: {
    color: '#457CA9',
  },
});

const CustomExcelUpload: React.FC<ExcelUploadProps> = ({
  sampleLink,
  sampleApi,
  classes,
  showButtonOnly,
  isDisabled,
  title,
  additionalUploadParams,
  t,
  onDataParsed,
  onRemove,
  firstRowAsHeader = true,
}) => {
  const [fileList, setFileList] = React.useState<UploadFile[]>([]);

  const customRequest = React.useCallback((args: CustomerRequestArgs) => {
    const { onSuccess, file } = args;
    setTimeout(() => {
      onSuccess(null, file);
    }, 100);
  }, []);

  const onFileRecieved = React.useCallback(async (file) => {
    const dataList = await handleXLSXFileWithoutMapping(file, firstRowAsHeader);

    if (onDataParsed) {
      onDataParsed(dataList, file);
    }
  }, [onDataParsed, firstRowAsHeader]);

  const handleUploadChange = React.useCallback((info: UploadChangeParam) => {
    const { file } = info;
    const { status } = file;
    if (status === 'done') {
      onFileRecieved(file.originFileObj);
    }
    setFileList([file]);
  }, [onFileRecieved]);

  const handleRemove = React.useCallback(() => {
    if (onRemove) {
      onRemove();
    }
    setFileList([]);
  }, [onRemove]);

  const uploadProps = React.useMemo(() => ({
    accept: '.xlsx, .xls, .csv',
    name: 'file',
    multiple: false,
    customRequest,
    showUploadList: { showPreviewIcon: false, showRemoveIcon: true },
    onRemove: handleRemove,
  }), [customRequest, handleRemove]);

  const mergedUploadProps = React.useMemo(
    () => ({ ...uploadProps, ...additionalUploadParams }),
    [uploadProps, additionalUploadParams],
  );

  return (
    <div>
      {showButtonOnly ? (
        <Upload
          fileList={fileList}
          onChange={handleUploadChange}
          {...mergedUploadProps}
        >
          <Button>{title || t('upload')}</Button>
        </Upload>
      ) : (
        <Dragger
          fileList={fileList}
          className={classes.dragger}
          onChange={handleUploadChange}
          {...mergedUploadProps}
        >
          <p className={classes.uploadHint} />
          <p className="ant-upload-drag-icon">
            <img className={classes.uploadIcon} src={uploadIcon} alt="upload" />
          </p>
          <p className={classes.uploadText}>
            <b>
              {t('Drag and drop to upload')} <br /> {t('or')}
              <span className={classes.browseText}> {t('browse')}</span> {t('to choose file')}
            </b>
          </p>
        </Dragger>
      )}
      {sampleLink && (
        <div
          className={
            isDisabled ? classNames(classes.disabled, classes.downloadDiv) : classes.downloadDiv
          }
        >
          <a href={sampleLink} target="_blank" rel="noreferrer">
            Download a sample file
          </a>
        </div>
      )}
      {sampleApi && (
        <div
          className={
            isDisabled ? classNames(classes.disabled, classes.downloadDiv) : classes.downloadDiv
          }
        >
          <span className={classes.link} onClick={() => sampleApi()}>
            Download a sample file
          </span>
        </div>
      )}
    </div>
  );
};

const ExcelUpload = withTranslation('translation')(CustomExcelUpload);
export default withStyles(styles, { injectTheme: true })(ExcelUpload);
